/**
 * 词云图组件
 *
 * 使用 ECharts + echarts-wordcloud 实现的词云图，用于展示关键词分布
 */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
// @ts-ignore - echarts-wordcloud 没有类型定义
import 'echarts-wordcloud';
import { WordCloudDataItem } from '@/types/analysis';
import './WordCloudChart.css';

interface WordCloudChartProps {
  data: WordCloudDataItem[];
  height?: number;
  title?: string;
  loading?: boolean;
}

export function WordCloudChart({ data, height = 400, title, loading = false }: WordCloudChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 转换数据格式为词云图所需格式
    const wordCloudData = data.map((item) => ({
      name: item.word,
      value: item.count,
      textStyle: {
        color: getSentimentColor(item.sentiment),
      },
    }));

    const option: echarts.EChartsCoreOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#1F1F1F',
        },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E8E8E8',
        borderWidth: 1,
        textStyle: {
          color: '#5C5C5C',
        },
        formatter: (params: any) => {
          const item = data.find((d) => d.word === params.name);
          const sentiment = item?.sentiment;
          const sentimentLabel = sentiment === 'positive' ? '正面' : sentiment === 'negative' ? '负面' : '中性';
          return `${params.name}<br/>出现次数: <strong>${params.value}</strong><br/>情感: ${sentimentLabel}`;
        },
      },
      series: [
        {
          type: 'wordCloud',
          shape: 'circle',
          keepAspect: false,
          left: 'center',
          top: title ? '20%' : 'center',
          width: '90%',
          height: title ? '75%' : '90%',
          right: null,
          bottom: null,
          sizeRange: [14, 60],
          rotationRange: [-45, 45],
          rotationStep: 45,
          gridSize: 8,
          drawOutOfBound: false,
          layoutAnimation: true,
          textStyle: {
            fontFamily: 'sans-serif',
            fontWeight: 'bold',
          },
          emphasis: {
            focus: 'self',
            textStyle: {
              textShadowBlur: 10,
              textShadowColor: '#333',
            },
          },
          data: wordCloudData,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, loading]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
    };
  }, []);

  if (loading) {
    return (
      <div className="wordcloud-chart-loading" style={{ height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="wordcloud-chart-empty" style={{ height }}>
        <div className="empty-message">暂无数据</div>
      </div>
    );
  }

  return <div ref={chartRef} className="wordcloud-chart" style={{ height }} />;
}

// 根据情感类型返回颜色
function getSentimentColor(sentiment?: string): string {
  switch (sentiment) {
    case 'positive':
      return '#52c41a'; // 绿色
    case 'negative':
      return '#ff4d4f'; // 红色
    case 'neutral':
      return '#1890ff'; // 蓝色
    default:
      return '#595959'; // 默认灰色
  }
}
